import React from "react";
import {
  Box,
  Popover,
  makeStyles,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch
} from "@material-ui/core";
import {
  setLayersHidden,
  setMarkersHidden,
  setMarkersClustered,
  setTilesDark,
  setSnapToRoadVisible,
  setUnscheduledMarkersHidden
} from "../../store/actions/map";
import clsx from "clsx";
import { connect } from "react-redux";
import TuneIcon from "@material-ui/icons/Tune";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import OptimizedTooltip from "../common/OptimizedTooltip";
import { remCalc, isEmptyObj } from "../../utils/helpers";
import { getCookie } from "../../utils/cookies";

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1.5),
    zIndex: "1306 !important"
  },
  list: {
    padding: theme.spacing(3, 2),
    "& > li:not(:last-child)": {
      marginBottom: theme.spacing(3)
    }
  },
  listItem: {
    padding: theme.spacing(0, 6, 0, 0),
    "& + div": {
      right: 0,
      top: -7,
      transform: "unset"
    }
  },
  itemText: {
    maxWidth: 272,
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    "& > p": {
      lineHeight: "1rem",
      color: theme.palette.secondary.main,
      marginTop: theme.spacing(0.25)
    }
  },
  contentWrapper: {
    backgroundColor: "#fff",
    boxSizing: "border-box",
    borderRadius: theme.variables.borderRadius,
    minHeight: 386,
    maxWidth: 360
  },
  themeWrapper: {
    backgroundColor: "#F7F7F7",
    border: "1px solid #eee",
    padding: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    justifyContent: "space-around",
    "& > button:first-child": {
      marginRight: theme.spacing(0.5)
    }
  },
  theme: {
    minWidth: 162,
    color: theme.palette.secondary.main,
    fontSize: "0.875rem",
    lineHeight: "1rem",
    fontWeight: 500,
    padding: theme.spacing(1.5, 0),
    "&:hover": {
      backgroundColor: "#eee",
      color: theme.palette.text.primary
    }
  },
  activeTheme: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    borderRadius: theme.spacing(0.5),
    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.16)"
  },
  divider: {
    backgroundColor: "#eee"
  },
  topButtonsDivider: {
    height: remCalc(24),
    alignSelf: "center",
    backgroundColor: "#eee"
  }
}));

function MapSettingsPopover({
  hasStops,
  iconClass,
  setTilesDark,
  snapToRoadEnabled,
  setSnapToRoadVisible,
  markersClustered,
  setMarkersClustered,
  markersHidden,
  setMarkersHidden,
  unscheduledMarkersHidden,
  setUnscheduledMarkersHidden,
  refitServicesBounds
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const classes = useStyles();

  const handleClick = event => {
    setAnchorEl(event.currentTarget.parentElement);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "map-popover" : undefined;

  const currentMapTheme = getCookie("maptheme") || "light";

  return (
    <>
      <OptimizedTooltip placement="bottom" title={"Map settings"}>
        <Box component={Button} className={iconClass} onClick={handleClick} id="map-settingButton">
          <TuneIcon />
        </Box>
      </OptimizedTooltip>
      <Divider orientation="vertical" className={classes.topButtonsDivider} />
      <OptimizedTooltip placement="bottom" title="Fit zoom">
        <Box
          component={Button}
          className={iconClass}
          onClick={refitServicesBounds}
          disabled={!hasStops}
          id="map-fitZoomButton"
        >
          <ZoomOutMapIcon />
        </Box>
      </OptimizedTooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{
          root: classes.root
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <div className={classes.contentWrapper}>
          <Box p={1.5}>
            <div className={classes.themeWrapper}>
              {["Light map", "Dark map"].map(item => {
                const theme = item.split(" ")[0].toLowerCase();
                return (
                  <Box
                    key={item}
                    component={Button}
                    className={clsx(classes.theme, {
                      [classes.activeTheme]: currentMapTheme === theme
                    })}
                    onClick={() => setTilesDark(theme)}
                    id={`map-setting-${theme}-map`}
                  >
                    {item}
                  </Box>
                );
              })}
            </div>
          </Box>
          <Divider className={classes.divider} />
          <List className={classes.list}>
            {/* Scheduled stops */}
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.itemText}
                primary="Show scheduled stops"
                id="mapSetting-scheduledStopText"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  color="primary"
                  onChange={() => setMarkersHidden(!markersHidden)}
                  checked={!markersHidden}
                  id="mapSetting-scheduledStopSwitch"
                />
              </ListItemSecondaryAction>
            </ListItem>
            {/* Unscheduled stops */}
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.itemText}
                primary="Show unscheduled stops"
                id="mapSetting-unscheduledStopText"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  color="primary"
                  onChange={() =>
                    setUnscheduledMarkersHidden(!unscheduledMarkersHidden)
                  }
                  checked={!unscheduledMarkersHidden}
                  id="mapSetting-unscheduledStopSwitch"
                />
              </ListItemSecondaryAction>
            </ListItem>
            {/* Cluster */}
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.itemText}
                primary="Cluster stops"
                secondary="When disabled, stop markers will not be clustered. This may lead to a slower performance."
                id="mapSetting-clusterStopsText"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  color="primary"
                  onChange={() => setMarkersClustered(!markersClustered)}
                  checked={markersClustered}
                  id="mapSetting-clusterStopsSwitch"
                />
              </ListItemSecondaryAction>
            </ListItem>
            {/* Snap to road */}
            <ListItem className={classes.listItem}>
              <ListItemText
                className={classes.itemText}
                primary="Show parking location"
                secondary="When disabled, the parking location will not be shown when hovering over a stop marker."
                id="mapSetting-parkingText"
              />
              <ListItemSecondaryAction>
                <Switch
                  edge="end"
                  color="primary"
                  onChange={() => setSnapToRoadVisible(!snapToRoadEnabled)}
                  checked={snapToRoadEnabled}
                  id="mapSetting-parkingSwitch"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </div>
      </Popover>
    </>
  );
}

const mapStateToProps = state => ({
  markersHidden: state.map.markersHidden,
  markersClustered: state.map.markersClustered,
  snapToRoadEnabled: state.map.snapToRoadEnabled,
  unscheduledMarkersHidden: state.map.unscheduledMarkersHidden,
  hasStops: state.project.vrp.stops.some(task => !isEmptyObj(task)),
  accountDrawerOpen: state.account.accountDrawerOpen
});

export default connect(mapStateToProps, {
  setMarkersHidden,
  setLayersHidden,
  setMarkersClustered,
  setTilesDark,
  setSnapToRoadVisible,
  setUnscheduledMarkersHidden
})(MapSettingsPopover);
